import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Компонент для отображения информационного тултипа
 * @param {string} text - Текст тултипа
 * @param {React.ReactNode} children - Элемент, при наведении на который показывается тултип
 * @param {string} position - Позиция тултипа: 'top' (по умолчанию) или 'bottom'
 */
const InfoTooltip = ({ text, children, position = 'top', width }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const triggerRef = useRef(null);
  const wrapped = Number.isFinite(width);
  const tooltipWidth = wrapped ? width : 200;

  useEffect(() => {
    if (isVisible && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipHeight = wrapped ? 72 : 30;
      const offset = 8; // Отступ от элемента

      let top, left;

      if (position === 'bottom') {
        top = triggerRect.bottom + offset + window.scrollY;
        left = triggerRect.left + (triggerRect.width / 2) - (tooltipWidth / 2) + window.scrollX;
      } else {
        top = triggerRect.top - tooltipHeight - offset + window.scrollY;
        left = triggerRect.left + (triggerRect.width / 2) - (tooltipWidth / 2) + window.scrollX;
      }

      // Корректировка если тултип выходит за границы экрана
      if (left < 0) left = 8;
      if (left + tooltipWidth > window.innerWidth) {
        left = window.innerWidth - tooltipWidth - 8;
      }

      setTooltipStyle({
        position: 'absolute',
        top: `${top}px`,
        left: `${left}px`,
        zIndex: 9999,
        ...(wrapped ? { width: tooltipWidth } : {}),
      });
    }
  }, [isVisible, position, tooltipWidth, wrapped]);

  const arrowClasses = position === 'bottom'
    ? 'bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-muted-900'
    : 'top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-muted-900';

  const tooltipContent = isVisible && (
    <div 
      style={tooltipStyle}
      className={`px-2 py-1 text-white bg-muted-900 rounded-lg shadow-lg pointer-events-none ${
        wrapped ? 'text-[11px] leading-snug whitespace-normal text-left' : 'text-[10px] whitespace-nowrap'
      }`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {text}
      <div className={`absolute ${arrowClasses}`}></div>
    </div>
  );

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className="inline-flex items-center justify-center"
      >
        {children}
      </span>
      {typeof document !== 'undefined' && createPortal(tooltipContent, document.body)}
    </>
  );
};

export default InfoTooltip;

